import { ref, watch } from 'vue';
import { searchCollection } from '@/services/discogs';
import debounce from 'lodash.debounce';

export default function useGlobalSearch() {
  const searchQuery = ref('');
  const searchResults = ref([]);
  const isSearching = ref(false);
  const hasSearched = ref(false);
  const searchError = ref(null);
  const currentPage = ref(1);
  const pagination = ref({});

  // Determine if we have more search results to load
  const hasMoreResults = () => currentPage.value < pagination.value?.pages;

  // Reset search state
  const resetSearch = () => {
    searchResults.value = [];
    hasSearched.value = false;
    searchError.value = null;
    currentPage.value = 1;
    pagination.value = {};
  };

  // Perform search with given query
  const performSearch = async (query, page = 1) => {
    if (!query) {
      resetSearch();
      return;
    }

    try {
      isSearching.value = true;
      searchError.value = null;

      // Explicitly set searchUserCollection to false for global search
      const response = await searchCollection(query, page, 10, false);

      if (page === 1) {
        searchResults.value = response.releases || [];
      } else {
        // Append results for infinite scrolling
        searchResults.value = [...searchResults.value, ...(response.releases || [])];
      }

      pagination.value = response.pagination || {};
      currentPage.value = page;
      hasSearched.value = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Search error:', error);
      searchError.value = error.message || 'An error occurred during search';
    } finally {
      isSearching.value = false;
    }
  };

  // Debounced search to avoid too many API calls while typing
  const debouncedSearch = debounce(query => {
    performSearch(query);
  }, 400);

  // Watch for changes to search query
  watch(searchQuery, newQuery => {
    if (newQuery) {
      debouncedSearch(newQuery);
    } else {
      resetSearch();
    }
  });

  // Load more search results (for infinite scrolling)
  const loadMoreResults = async () => {
    if (hasMoreResults() && !isSearching.value && searchQuery.value) {
      await performSearch(searchQuery.value, currentPage.value + 1);
    }
  };

  return {
    searchQuery,
    searchResults,
    isSearching,
    hasSearched,
    searchError,
    performSearch,
    resetSearch,
    loadMoreResults,
    hasMoreResults,
  };
}
